"use client";

import React from "react";
import { Check } from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
} from "@/components/ui/card";
import type { DesignerApplicationFormData } from "@/lib/validations/designer-application";
import { FieldErrors } from "react-hook-form";
import { APPLY_BODY, APPLY_PROGRESS_LABEL, applyHeading } from "./applyCopy";

interface ApplicationProgressSidebarProps {
  watchedFields: Partial<DesignerApplicationFormData>;
  errors: FieldErrors<DesignerApplicationFormData>;
  /** Set on /apply/<code> pages — shows job-application copy instead of /join's */
  jobTitle?: string;
}

interface SectionStatus {
  name: string;
  isComplete: boolean;
  isInProgress: boolean;
}

const ApplicationProgressSidebar: React.FC<ApplicationProgressSidebarProps> = ({
  watchedFields,
  errors,
  jobTitle,
}) => {
  // Determine section completion status
  const isPersonalInfoComplete = (): boolean => {
    return !!(
      watchedFields.fullName &&
      watchedFields.email &&
      watchedFields.phoneCountryCode &&
      watchedFields.phoneNumber &&
      watchedFields.openToRoles &&
      watchedFields.country &&
      watchedFields.city &&
      !errors.fullName &&
      !errors.email &&
      !errors.phoneCountryCode &&
      !errors.phoneNumber &&
      !errors.openToRoles &&
      !errors.country &&
      !errors.city
    );
  };

  const isRoleExperienceComplete = (): boolean => {
    const hasPortfolioPassword = watchedFields.portfolioPassword === "yes";
    const hasPortfolioPasswordValue = hasPortfolioPassword
      ? !!watchedFields.portfolioPasswordValue
      : true;

    return !!(
      watchedFields.portfolioLink &&
      watchedFields.portfolioPassword &&
      watchedFields.linkedInProfile &&
      watchedFields.currentRoleLevel &&
      watchedFields.languages &&
      watchedFields.languages.length > 0 &&
      hasPortfolioPasswordValue &&
      !errors.portfolioLink &&
      !errors.portfolioPassword &&
      !errors.portfolioPasswordValue &&
      !errors.linkedInProfile &&
      !errors.currentRoleLevel &&
      !errors.languages
    );
  };

  const isWorkPreferencesComplete = (): boolean => {
    return !!(
      watchedFields.nextRoleLevel &&
      watchedFields.workSetup &&
      watchedFields.workSetup.length > 0 &&
      watchedFields.workEnvironment &&
      watchedFields.workEnvironment.length > 0 &&
      watchedFields.expectedSalaryRange &&
      !errors.nextRoleLevel &&
      !errors.workSetup &&
      !errors.workEnvironment &&
      !errors.expectedSalaryRange
    );
  };

  const isAvailabilityComplete = (): boolean => {
    return !!(
      watchedFields.readyToBegin &&
      watchedFields.openToFutureContact &&
      !errors.readyToBegin &&
      !errors.openToFutureContact
    );
  };

  const isLegalAgreementsComplete = (): boolean => {
    return !!(
      watchedFields.acceptedTermsOfService &&
      watchedFields.acceptedPrivacyPolicy &&
      watchedFields.acceptedCookiePolicy &&
      !errors.acceptedTermsOfService &&
      !errors.acceptedPrivacyPolicy &&
      !errors.acceptedCookiePolicy
    );
  };

  // Determine if sections are in progress (have some fields filled but not complete)
  const isPersonalInfoInProgress = (): boolean => {
    if (isPersonalInfoComplete()) return false;
    return !!(
      watchedFields.fullName ||
      watchedFields.email ||
      watchedFields.phoneCountryCode ||
      watchedFields.phoneNumber ||
      watchedFields.openToRoles ||
      watchedFields.country ||
      watchedFields.city
    );
  };

  const isRoleExperienceInProgress = (): boolean => {
    if (isRoleExperienceComplete()) return false;
    return !!(
      watchedFields.portfolioLink ||
      watchedFields.portfolioPassword ||
      watchedFields.portfolioPasswordValue ||
      watchedFields.linkedInProfile ||
      watchedFields.currentRoleLevel ||
      (watchedFields.languages && watchedFields.languages.length > 0)
    );
  };

  const isWorkPreferencesInProgress = (): boolean => {
    if (isWorkPreferencesComplete()) return false;
    return !!(
      watchedFields.nextRoleLevel ||
      (watchedFields.workSetup && watchedFields.workSetup.length > 0) ||
      (watchedFields.workEnvironment && watchedFields.workEnvironment.length > 0) ||
      watchedFields.expectedSalaryRange
    );
  };

  const isAvailabilityInProgress = (): boolean => {
    if (isAvailabilityComplete()) return false;
    return !!(
      watchedFields.readyToBegin ||
      watchedFields.openToFutureContact
    );
  };

  const isLegalAgreementsInProgress = (): boolean => {
    if (isLegalAgreementsComplete()) return false;
    return !!(
      watchedFields.acceptedTermsOfService ||
      watchedFields.acceptedPrivacyPolicy ||
      watchedFields.acceptedCookiePolicy
    );
  };

  const sections: SectionStatus[] = [
    { 
      name: "Personal Information", 
      isComplete: isPersonalInfoComplete(),
      isInProgress: isPersonalInfoInProgress()
    },
    { 
      name: "Role & Experience", 
      isComplete: isRoleExperienceComplete(),
      isInProgress: isRoleExperienceInProgress()
    },
    { 
      name: "Work Preferences", 
      isComplete: isWorkPreferencesComplete(),
      isInProgress: isWorkPreferencesInProgress()
    },
    { 
      name: "Availability", 
      isComplete: isAvailabilityComplete(),
      isInProgress: isAvailabilityInProgress()
    },
    { 
      name: "Legal Agreements", 
      isComplete: isLegalAgreementsComplete(),
      isInProgress: isLegalAgreementsInProgress()
    },
  ];

  const completedCount = sections.filter((s) => s.isComplete).length;
  const totalSections = sections.length;

  return (
    <Card className="bg-[#FAFAF9] border-none shadow-none rounded-none p-0 px-4 items-stretch rounded-l-lg pt-6 h-full hidden lg:flex flex-col max-h-[calc(100vh-80px)]">
      <CardHeader className="p-6 mb-2">
        <h2
          className="text-lg md:text-2xl font-normal leading-tight tracking-tight font-prociono"
          style={{
            background: "linear-gradient(90deg, #231617 0%, #89565A 100%)",
            backgroundClip: "text",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          {jobTitle ? applyHeading(jobTitle) : "Join our designer network."}
        </h2>
        <p className="text-sm text-gray-600 mt-2">{jobTitle ? APPLY_BODY : "We review designer profiles for the roles we're recruiting for. Share your experience, preferences, and the work you want next so we can consider you for relevant opportunities."}</p>
      </CardHeader>
      <CardContent className="p-6 pt-0 flex flex-col gap-6 flex-1">
        <div className="flex flex-col gap-1">
          <h3 className="text-gray-600 mb-1">{jobTitle ? APPLY_PROGRESS_LABEL : "Profile Progress"}</h3>
          <p className="text-gray-500 text-sm"> Takes 2 minutes to complete </p>
          <p className="text-[#FA5D29] text-sm">
            {completedCount}/{totalSections} sections complete
          </p>
        </div>
        <div className="space-y-0">
          {sections.map((section, index) => {
            const isCompleted = section.isComplete;
            const isInProgress = section.isInProgress && !isCompleted;
            const isNotStarted = !isCompleted && !isInProgress;

            return (
              <div key={section.name} className="flex items-start gap-3">
                {/* Icon and connecting line */}
                <div className="flex flex-col items-center flex-shrink-0">
                  <div className="flex items-center justify-center w-5 h-5 flex-shrink-0">
                    {isCompleted ? (
                      // Completed: Orange circle with white checkmark
                      <div className="w-5 h-5 rounded-full bg-[#FA5D29] flex items-center justify-center">
                        <Check className="w-3 h-3 text-white" strokeWidth={3} />
                      </div>
                    ) : isInProgress ? (
                      // In progress: Orange circle with border and orange dot
                      <div className="w-5 h-5 rounded-full bg-white flex items-center justify-center border border-orange-500">
                        <div className="w-2.5 h-2.5 rounded-full bg-orange-600" />
                      </div>
                    ) : (
                      // Not started: Light gray outline circle with no dot
                      <span className="w-5 h-5 rounded-full border-2 border-gray-300 bg-white"></span>
                    )}
                  </div>
                  {/* Vertical line connecting sections */}
                  {index < sections.length - 1 && (
                    <div
                      className={`h-10 w-0.5 ${
                        isCompleted || isInProgress
                          ? "bg-orange-500"
                          : "bg-gray-200"
                      }`}
                    />
                  )}
                </div>
                {/* Section name */}
                <div className="flex-1 pt-0.5">
                  <p
                    className={`text-sm leading-relaxed ${
                      isInProgress
                        ? "text-orange-500 font-medium"
                        : isCompleted
                        ? "text-gray-500"
                        : "text-gray-500"
                    }`}
                  >
                    {section.name}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export default ApplicationProgressSidebar;
